/**
 * API client module for communicating with the backend.
 *
 * The base URL is configurable via the VITE_API_URL environment variable
 * with a fallback to '/api' for proxied development setups.
 */

import type { ApiSuccessResponse, ApiErrorResponse } from '@gbty/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

/** Error thrown when an API request fails */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Generic fetch wrapper that handles the API envelope format.
 * Returns the unwrapped `data` on success, or throws an ApiError on failure.
 */
export async function apiFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const response = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    let errorCode: string | undefined;

    try {
      const errorBody = (await response.json()) as ApiErrorResponse;
      if (errorBody.error) {
        errorMessage = errorBody.error.message;
        errorCode = errorBody.error.code;
      }
    } catch {
      // Response body was not JSON; use the default error message
    }

    throw new ApiError(errorMessage, response.status, errorCode);
  }

  const body = (await response.json()) as ApiSuccessResponse<T>;
  return body.data;
}

/**
 * Admin API fetch wrapper that adds JWT Authorization header.
 *
 * Requires a token getter function (from AuthContext.getIdToken).
 * Throws ApiError with status 401 if no token is available.
 */
export async function adminApiFetch<T>(
  path: string,
  getToken: () => string | null,
  init?: RequestInit,
): Promise<T> {
  const token = getToken();
  if (!token) {
    throw new ApiError('Authentication is required.', 401, 'UNAUTHORIZED');
  }

  return apiFetch<T>(path, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${token}`,
    },
  });
}
